import React from 'react';
import {
  ChevronUp,
  ChevronDown,
  ChevronsLeft,
  ChevronsRight,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react';
import { Skeleton } from 'components/ui/skeleton';
import { useSmoothLoading } from 'lib/use-smooth-loading';
import type { PendingPodRow } from '../api';
import { NotesModal } from './NotesModal';

type SortDirection = 'asc' | 'desc';

type ColumnDef = {
  key: keyof PendingPodRow | 'actions';
  label: string;
  isNumeric?: boolean;
};

interface Props {
  data: PendingPodRow[];
  loading: boolean;
  page: number;
  pageSize: number;
  totalItems: number;
  sortBy: keyof PendingPodRow;
  sortOrder: SortDirection;
  onSortChange: (column: keyof PendingPodRow) => void;
  onPageChange: (page: number) => void;
  onPageSizeChange: (size: number) => void;
}

const columns: ColumnDef[] = [
  { key: 'cn_number', label: 'CN Number' },
  { key: 'consigner', label: 'Consigner' },
  { key: 'consignee', label: 'Consignee' },
  { key: 'delivery_lhc_number', label: 'Delivery LHC No.' },
  { key: 'delivery_lhc_date', label: 'Delivery LHC Date' },
  { key: 'delivery_lhc_branch', label: 'Delivery LHC Branch' },
  { key: 'vehicle_type', label: 'Vehicle Type' },
  { key: 'vehicle_number', label: 'Vehicle Number' },
  { key: 'lhc_origin', label: 'LHC Origin' },
  { key: 'lhc_destination', label: 'LHC Destination' },
  { key: 'delivery_date', label: 'Delivery Date' },
  { key: 'pod_pending_days', label: 'POD Pending (days)', isNumeric: true },
  { key: 'pod_submission_deadline', label: 'POD Submission Deadline' },
  { key: 'pod_delay_days', label: 'POD Delay Days', isNumeric: true },
  { key: 'vendor_type', label: 'Vendor Type' },
  { key: 'driver_number', label: 'Driver Number' },
  { key: 'vendor_name', label: 'Vendor Name' },
  { key: 'actions', label: 'Actions' },
];

const PendingPODTable: React.FC<Props> = ({
  data,
  loading,
  page,
  pageSize,
  totalItems,
  sortBy,
  sortOrder,
  onSortChange,
  onPageChange,
  onPageSizeChange,
}) => {
  const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));
  const { isLoading } = useSmoothLoading(loading, {
    minLoadingTime: 150,
    debounceTime: 150,
  });

  const formatValue = (value: PendingPodRow[keyof PendingPodRow], column: keyof PendingPodRow, isNumeric?: boolean) => {
    if (value === null || value === undefined || value === '') return '-';
    if (typeof value === 'number') {
      if (!isNumeric) return String(value);

      if (column === 'pod_pending_days' || column === 'pod_delay_days') {
        return value.toLocaleString('en-IN', {
          maximumFractionDigits: 0,
        });
      }

      return value.toLocaleString('en-IN', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    }
    if (typeof value === 'string') {
      const parsed = new Date(value);
      if (!Number.isNaN(parsed.getTime()) && /^\d{4}-\d{2}-\d{2}/.test(value)) {
        return parsed.toLocaleDateString('en-GB', {
          day: '2-digit',
          month: 'short',
          year: 'numeric',
        });
      }
      return value;
    }
    return String(value);
  };

  const renderSkeletonRows = () =>
    Array.from({ length: 10 }).map((_, rowIndex) => (
      <tr key={`pending-pod-skeleton-${rowIndex}`}>
        {columns.map((col, colIndex) => (
          <td key={`${rowIndex}-${colIndex}`} className="border border-gray-200 px-2 py-1.5">
            {col.key === 'actions' ? (
              <Skeleton className="h-5 w-10 mx-auto rounded" />
            ) : (
              <Skeleton className={`h-4 ${colIndex === 0 ? 'w-3/4' : 'w-full'}`} />
            )}
          </td>
        ))}
      </tr>
    ));

  return (
    <div className="flex flex-1 min-h-0 flex-col">
      <div className="min-h-0 flex-1 overflow-auto border border-gray-200">
        <table className="w-full border-collapse text-sm">
          <thead>
            <tr className="bg-gray-100">
              {columns.map((col) => {
                const isActions = col.key === 'actions';
                const active = sortBy === col.key;

                return (
                  <th
                    key={String(col.key)}
                    onClick={() => !isActions && onSortChange(col.key as keyof PendingPodRow)}
                    className={`border border-gray-300 px-2 py-2 whitespace-nowrap font-semibold ${!isActions ? 'cursor-pointer' : ''}
                      ${col.isNumeric ? 'text-right' : 'text-left'}`}
                  >
                    <div className={`flex items-center gap-1 ${col.isNumeric ? 'justify-end' : 'justify-start'}`}>
                      {col.label}
                      {!isActions && (active ? (
                        sortOrder === 'asc' ? <ChevronUp size={14} /> : <ChevronDown size={14} />
                      ) : (
                        <ChevronUp size={14} className="text-gray-300" />
                      ))}
                    </div>
                  </th>
                );
              })}
            </tr>
          </thead>

          <tbody>
            {isLoading ? (
              renderSkeletonRows()
            ) : data.length === 0 ? (
              <tr>
                <td colSpan={columns.length} className="text-center py-16 text-gray-500">
                  No data available
                </td>
              </tr>
            ) : (
              data.map((row, idx) => (
                <tr key={row.cn_number || idx} className="hover:bg-gray-50">
                  {columns.map((col) => {
                    if (col.key === 'actions') {
                      return (
                        <td key="actions" className="border border-gray-200 px-2 py-1.5 whitespace-nowrap text-center">
                          <NotesModal cnNumber={row.cn_number} />
                        </td>
                      );
                    }
                    const isDelayCell = col.key === 'pod_delay_days';
                    const delayDays = row.pod_delay_days;

                    return (
                      <td
                        key={String(col.key)}
                        className={`border border-gray-200 px-2 py-1.5 whitespace-nowrap ${col.isNumeric ? 'text-right' : 'text-left'}  ${isDelayCell && delayDays !== null && delayDays !== undefined ? 'bg-red-50 text-red-600 font-semibold' : ''}`}
                      >
                        {formatValue(row[col.key], col.key, col.isNumeric)}
                      </td>
                    );
                  })}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <div className="shrink-0 flex items-center justify-between pt-4">
        <span className="text-sm text-gray-600">
          Showing{' '}
          <strong>
            {totalItems === 0 ? 0 : (page - 1) * pageSize + 1}
          </strong>{' '}
          to{' '}
          <strong>{Math.min(page * pageSize, totalItems)}</strong> of{' '}
          <strong>{totalItems}</strong>
        </span>

        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2 text-sm">
            Rows per page
            <select
              value={pageSize}
              onChange={(e) => onPageSizeChange(Number(e.target.value))}
              className="border rounded px-2 py-1"
            >
              {[10, 20, 50, 100].map((size) => (
                <option key={size} value={size}>
                  {size}
                </option>
              ))}
            </select>
          </div>

          <div className="flex items-center gap-1">
            <button
              disabled={page === 1}
              onClick={() => onPageChange(1)}
              className="p-1 border rounded disabled:opacity-50"
            >
              <ChevronsLeft size={16} />
            </button>

            <button
              disabled={page === 1}
              onClick={() => onPageChange(page - 1)}
              className="p-1 border rounded disabled:opacity-50"
            >
              <ChevronLeft size={16} />
            </button>

            <span className="px-2 text-sm">
              Page <strong>{page}</strong> / {totalPages}
            </span>

            <button
              disabled={page === totalPages}
              onClick={() => onPageChange(page + 1)}
              className="p-1 border rounded disabled:opacity-50"
            >
              <ChevronRight size={16} />
            </button>

            <button
              disabled={page === totalPages}
              onClick={() => onPageChange(totalPages)}
              className="p-1 border rounded disabled:opacity-50"
            >
              <ChevronsRight size={16} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PendingPODTable;
